import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Dropdown,
} from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectTokens } from "../redux/userSlice";
import LoadingAnimation from "./LoadingAnimation";
import "./UserManagementComponent.css";

// Must match the `applications.app_name` row for Sheriff Sale, which is also the
// key AppPortal.js uses to decide whether to show that app's tile.
const SHERIFF_SALE_APP_NAME = "SHERIFF";

// Sheriff Sale scopes access by a second field for these roles. Provisioning one
// without its scope leaves the user unfiltered, so core-api rejects it too.
const SHERIFF_SALE_SCOPED_ROLES = {
  "Law Firm": "lawFirm",
  Sheriff: "county",
};

const SHERIFF_SALE_INTERNAL_ROLES = ["Admin", "CSR"];

const UserEditModal = ({
  show,
  handleClose,
  selectedUser,
  setShowToast,
  setToastInfo,
}) => {
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [userDetails, setUserDetails] = useState({
    user: {
      fusionauth_user_id: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      phone: "",
      fax: "",
      addresses: [
        {
          street_address: "",
          street_address2: "",
          city: "",
          state: "",
          zip: "",
        },
      ],
    },
    organizations: [
      {
        business_name: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        phone: "",
        fax: "",
        addresses: [
          {
            street_address: "",
            street_address2: "",
            city: "",
            state: "",
            zip: "",
          },
        ],
      },
    ],
  });

  const emptyAddress = {
    street_address: "",
    street_address2: "",
    city: "",
    state: "",
    zip: "",
  };

  const emptyOrg = {
    business_name: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    phone: "",
    fax: "",
    addresses: [emptyAddress], // Initialize with one empty address
  };

  const [applications, setApplications] = useState([]);
  const [appAccess, setAppAccess] = useState({});
  const [appRoles, setAppRoles] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const tokens = useSelector(selectTokens);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const coreApiUrl = process.env.REACT_APP_CORE_API_BASE_URL;
  const apiKey = process.env.REACT_APP_SERVER_API_KEY;

  // Sheriff Sale role + scope. Sourced from core-api rather than the
  // usermanagement `roles`/`states`/`counties` tables — those are a separate id
  // space, so their ids can't be written to the Sheriff Sale user row.
  const [sssRoles, setSssRoles] = useState([]);
  const [sssStates, setSssStates] = useState([]);
  const [sssCounties, setSssCounties] = useState([]);
  const [sssLawFirms, setSssLawFirms] = useState([]);
  const [sssLawFirmId, setSssLawFirmId] = useState(null);
  // The role this user already holds in the Sheriff Sale system, if any.
  const [sssUserRoleName, setSssUserRoleName] = useState(null);
  // Whether Sheriff Sale was already granted when the modal opened. Revoking is
  // driven off the enabled → disabled transition, so opening an unrelated user
  // and saving can't deactivate an SSS account that was never portal-managed.
  const [sheriffSaleWasEnabled, setSheriffSaleWasEnabled] = useState(false);
  const [, setUpdateStatus] = useState({
    success: false,
    message: "",
  });


  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userDetailsResponse = await axios.get(
          `${baseUrl}/app/me/userinfo/${selectedUser.fusionauth_user_id}`,
          {
            headers: { Authorization: `Bearer ${tokens.accessToken}` },
          }
        );

        const userData = userDetailsResponse.data || {
          user: {},
          organizations: [],
        };
        setUserDetails(userData);

        if (userData.user.county_id && userData.user.state_id) {
          const stateResponse = await axios.get(
            `${baseUrl}/app/access/states`
          );
          const userState = stateResponse.data.find(
            (s) => s.state_id === userData.user.state_id
          );
          setSelectedState(userState);

          if (userState) {
            const countiesResponse = await fetchCounties(userState.state_id);
            const userCounty = countiesResponse.find(
              (c) => c.county_id === userData.user.county_id
            );
            setSelectedCounty(userCounty || null);
          }
        } else {
          setSelectedCounty(null);
          setSelectedState(null);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedState) {
      fetchCounties(selectedState.state_id);
    }
  }, [selectedState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [appsResponse, rolesResponse] = await Promise.all([
          axios.get(`${baseUrl}/app/access/applications`, {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
              "x-api-key": apiKey,
            },
          }),
          axios.get(`${baseUrl}/app/access/roles`, {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
              "x-api-key": apiKey,
            },
          }),
        ]);

        setApplications(appsResponse.data);
        const rolesByApp = rolesResponse.data.reduce((acc, role) => {
          acc[role.app_id] = (acc[role.app_id] || []).concat(role);
          return acc;
        }, {});

        setAppRoles(rolesByApp);
        const accessByApp = appsResponse.data.reduce((acc, app) => {
          acc[app.app_name] = { enabled: false, role: "Select Role" };
          return acc;
        }, {});

        const response = await axios.get(
          `${baseUrl}/app/access/user-apps-roles/${selectedUser.fusionauth_user_id}`,
          {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
              "x-api-key": apiKey,
            },
          }
        );

        const userApps = response.data;

        const userAppAccess = userApps.reduce((acc, app) => {
          acc[app.app_name] = {
            enabled: true,
            role: app.role_name,
          };
          return acc;
        }, {});

        setAppAccess({ ...accessByApp, ...userAppAccess });
        setSheriffSaleWasEnabled(
          Boolean(userAppAccess[SHERIFF_SALE_APP_NAME]?.enabled)
        );
      } catch (error) {
        console.error("Error loading applications and roles:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [baseUrl, tokens, apiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Fetched independently — one failing shouldn't blank the other.
    const fetchSheriffSaleOptions = async () => {
      try {
        const rolesRes = await axios.get(`${coreApiUrl}/sss/roles`, {
          headers: { Authorization: `Bearer ${tokens.accessToken}` },
        });
        setSssRoles(rolesRes.data?.data || []);
      } catch (error) {
        console.error("Error loading Sheriff Sale roles:", error);
      }

      try {
        const statesRes = await axios.get(`${coreApiUrl}/sss/states`);
        setSssStates(statesRes.data?.data || []);
      } catch (error) {
        console.error("Error loading Sheriff Sale states:", error);
      }

      try {
        const lawFirmsRes = await axios.get(`${coreApiUrl}/sss/lawfirms`, {
          headers: { Authorization: `Bearer ${tokens.accessToken}` },
        });
        setSssLawFirms(lawFirmsRes.data?.data || []);
      } catch (error) {
        console.error("Error loading Sheriff Sale law firms:", error);
      }
    };

    fetchSheriffSaleOptions();
  }, [coreApiUrl, tokens]); // eslint-disable-line react-hooks/exhaustive-deps

  const sheriffSaleEnabled =
    appAccess[SHERIFF_SALE_APP_NAME]?.enabled || false;

  // The Sheriff Sale app's dropdown offers SSS role names (see the Dropdown.Menu
  // below), unlike every other app whose names come from usermanagement. Only
  // trust the appAccess value when it actually is one: user-apps-roles seeds it
  // with the generic usermanagement role ("Sheriff Sale User"), which exists to
  // grant the portal tile and is not a role anyone chose here. Anything else
  // falls back to the role the Sheriff Sale system already has.
  const pickedSssRoleName = appAccess[SHERIFF_SALE_APP_NAME]?.role;
  const sssRoleName =
    (sssRoles.some((role) => role.name === pickedSssRoleName)
      ? pickedSssRoleName
      : sssUserRoleName) || null;
  const sssRoleId =
    sssRoles.find((role) => role.name === sssRoleName)?.idRole ?? null;
  const sssRequiredScope = sssRoleName
    ? SHERIFF_SALE_SCOPED_ROLES[sssRoleName]
    : null;

  // The state/county controls above hold usermanagement ids, which are a
  // different id space from the Sheriff Sale tables. Resolve by name instead —
  // an exact match or nothing, so a near-miss fails the save rather than
  // silently assigning the wrong county.
  // The two systems name the same place differently: usermanagement stores
  // "Adams" / "St. James", the Sheriff Sale DB stores "Adams County",
  // "Plaquemines Parish", even "St. James Parish Sheriff's Office". Compare on
  // the part before the county/parish word, with punctuation and spacing
  // flattened, so "St. James" and "St James Parish Sheriff's Office" agree.
  const normalizeName = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/\./g, "")
      .split(/\b(?:county|parish)\b/)[0]
      .trim()
      .replace(/\s+/g, " ");

  const sssStateId =
    sssStates.find(
      (state) =>
        normalizeName(state.LongName) === normalizeName(selectedState?.state_name)
    )?.idState ?? null;

  const sssCountyId =
    sssCounties.find(
      (county) =>
        normalizeName(county.LongName) === normalizeName(selectedCounty?.county_name)
    )?.idCounty ?? null;

  useEffect(() => {
    if (!sssStateId) {
      setSssCounties([]);
      return;
    }

    const fetchSssCounties = async () => {
      try {
        const response = await axios.post(`${coreApiUrl}/sss/counties`, {
          stateId: sssStateId,
        });
        setSssCounties(response.data?.data || []);
      } catch (error) {
        console.error("Error loading Sheriff Sale counties:", error);
        setSssCounties([]);
      }
    };

    fetchSssCounties();
  }, [coreApiUrl, sssStateId]);

  useEffect(() => {
    if (!selectedUser?.fusionauth_user_id) return;

    const fetchExistingSssUser = async () => {
      try {
        const response = await axios.get(`${coreApiUrl}/sss/users/lookup`, {
          headers: { Authorization: `Bearer ${tokens.accessToken}` },
          params: { fusionauthSub: selectedUser.fusionauth_user_id },
        });

        const existing = response.data?.data;
        if (!existing) return;

        // Carried through the save so re-saving doesn't clear a firm that was
        // set in the Sheriff Sale app — there's no law firm control here.
        setSssLawFirmId(existing.lawFirmId ?? null);

        // Held separately rather than written into appAccess: that state is
        // replaced wholesale when the applications fetch resolves, and these two
        // requests finish in either order.
        setSssUserRoleName(existing.roleName ?? null);
      } catch (error) {
        console.error("Error loading existing Sheriff Sale user:", error);
      }
    };

    fetchExistingSssUser();
  }, [coreApiUrl, selectedUser, tokens]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Mirrors the server-side checks in core-api's provisionUser. A missing scope
   * doesn't narrow the user's access downstream — it removes the filter — so
   * both sides refuse rather than provisioning a half-configured account.
   */
  const validateSheriffSale = () => {
    if (!sheriffSaleEnabled) return null;
    if (!sssRoleId) return "Select a Sheriff Sale role.";
    if (sssRequiredScope === "lawFirm" && !sssLawFirmId) {
      return sssLawFirms.length === 0
        ? "No Sheriff Sale law firms loaded — try reopening this user."
        : "Law Firm users need a law firm selected above.";
    }

    // Checked before the county, because the county list is fetched for the
    // resolved state — an unresolved state empties it and would otherwise be
    // reported as a county that doesn't exist.
    if (!SHERIFF_SALE_INTERNAL_ROLES.includes(sssRoleName) && !sssStateId) {
      return selectedState
        ? `"${selectedState.state_name}" doesn't match a state in the Sheriff Sale system.`
        : `${sssRoleName} users need a state selected above.`;
    }

    if (sssRequiredScope === "county" && !sssCountyId) {
      if (!selectedCounty) {
        return "Sheriff users need a county or parish selected above.";
      }
      return sssCounties.length === 0
        ? `No Sheriff Sale counties loaded for ${selectedState?.state_name ?? "this state"} — try reopening this user.`
        : `"${selectedCounty.county_name}" doesn't match a county in the Sheriff Sale system.`;
    }
    return null;
  };

  /**
   * Writes the Sheriff Sale role to the SSS user row, creating and
   * FusionAuth-linking it when the user has no row yet. Runs alongside the
   * usermanagement `user_roles` write, which stays the source of app access.
   */
  const saveSheriffSaleRole = async () => {
    const headers = { Authorization: `Bearer ${tokens.accessToken}` };

    if (!sheriffSaleEnabled) {
      if (!sheriffSaleWasEnabled) return;
      await axios.post(
        `${coreApiUrl}/sss/users/revoke`,
        { fusionauthSub: selectedUser.fusionauth_user_id, email: userDetails.user.email },
        { headers }
      );
      return;
    }

    await axios.post(
      `${coreApiUrl}/sss/users/provision`,
      {
        fusionauthSub: selectedUser.fusionauth_user_id,
        email: userDetails.user.email,
        firstName: userDetails.user.first_name,
        lastName: userDetails.user.last_name,
        phone: userDetails.user.phone,
        roleId: sssRoleId,
        selectedStateId: sssStateId,
        countyId: sssRequiredScope === "county" ? sssCountyId : null,
        lawFirmId: sssRequiredScope === "lawFirm" ? sssLawFirmId : null,
        active: 1,
      },
      { headers }
    );
  };


  useEffect(() => {
    if (selectedUser) {
      setUserDetails((prevDetails) => {
        const updatedDetails = { ...prevDetails };

        // Ensures there is always at least one address for the user
        if (updatedDetails.user.addresses.length === 0) {
          updatedDetails.user.addresses = [{ ...emptyAddress }];
        }

        // Ensures there is always at least one organization
        // and each organization has at least one address
        if (updatedDetails.organizations.length === 0) {
          updatedDetails.organizations = [{ ...emptyOrg }];
        } else {
          updatedDetails.organizations = updatedDetails.organizations.map(
            (org) => {
              if (org.addresses.length === 0) {
                org.addresses = [{ ...emptyAddress }];
              }
              return org;
            }
          );
        }

        return updatedDetails;
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCounties = async (stateId) => {
    if (!stateId) {
      setCounties([]);
      return [];
    }
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/app/access/counties/state/${stateId}`,
        {
          headers: { Authorization: `Bearer ${tokens.accessToken}` },
        }
      );
      setCounties(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching counties:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const clearCountyStateSelection = () => {
    setSelectedState(null);
    setSelectedCounty(null);
  };

  const fetchStates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/app/access/states`, {
        headers: { Authorization: `Bearer ${tokens.accessToken}` },
      });
      setStates(response.data);
    } catch (error) {
      console.error("Error fetching states:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppToggle = (appName) => {
    setAppAccess((prevAppAccess) => ({
      ...prevAppAccess,
      [appName]: {
        ...prevAppAccess[appName],
        enabled: !prevAppAccess[appName]?.enabled,
      },
    }));
  };

  const handleRoleChange = (appName, role) => {
    setAppAccess((prevAppAccess) => ({
      ...prevAppAccess,
      [appName]: {
        ...prevAppAccess[appName],
        role: role,
      },
    }));
  };

  // Handler for user details, including nested addresses
  const handleUserDetailChange = (event, index, key) => {
    const { name, value } = event.target;

    if (key) {
      // This is for handling nested address updates
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        user: {
          ...prevDetails.user,
          addresses: prevDetails.user.addresses.map((address, addrIdx) => {
            if (index === addrIdx) {
              return { ...address, [key]: value };
            }
            return address;
          }),
        },
      }));
    } else {
      // This is for handling direct property updates like first_name, last_name
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        user: {
          ...prevDetails.user,
          [name]: value,
        },
      }));
    }
  };

  // Handler for organization details, including nested addresses
  const handleOrganizationDetailChange = (event, orgIndex, addrIndex, key) => {
    const { name, value } = event.target;
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      organizations: prevDetails.organizations.map((org, oIdx) => {
        if (oIdx === orgIndex) {
          if (key) {
            // Handle nested address update
            return {
              ...org,
              addresses: org.addresses.map((address, aIdx) => {
                if (aIdx === addrIndex) {
                  return { ...address, [key]: value };
                }
                return address;
              }),
            };
          } else {
            // Handle direct org property update
            return { ...org, [name]: value };
          }
        }
        return org;
      }),
    }));
  };

  const handleStateSelect = (eventKey) => {
    const state = states.find((s) => s.state_id.toString() === eventKey);
    setSelectedState(state);
    setSelectedCounty(null); // Reset county selection when state changes
  };

  const handleCountySelect = (eventKey) => {
    const county = counties.find((c) => c.county_id.toString() === eventKey);
    setSelectedCounty(county);
  };

  const updateUserDetails = async () => {
    const sheriffSaleError = validateSheriffSale();
    if (sheriffSaleError) {

      setShowToast(true);
      setToastInfo({ header: "Sheriff Sale", body: sheriffSaleError });
      return;
    }

    setIsLoading(true);

    // Extract user data without addresses and notifications
    const {
      addresses: userAddresses,
      notifications,
      ...userDataWithoutAddressesAndNotifications
    } = userDetails.user;

    // Prepare organization data without addresses and extract org addresses
    const orgData = userDetails.organizations.map((org) => {
      const { addresses, ...orgDataWithoutAddresses } = org;
      return orgDataWithoutAddresses;
    });

    const orgAddresses = userDetails.organizations.map((org) => {
      return org.addresses.length > 0 ? org.addresses[0] : null; // Assuming sending the first address only
    });

    // Prepare payload according to your backend requirements
    const payload = {
      userData: { ...userDataWithoutAddressesAndNotifications },
      userAddress: userAddresses.length > 0 ? { ...userAddresses[0] } : null, // Assuming sending the first address only
      orgData: orgData, // Now orgData is an array
      orgAddress: orgAddresses[0], // Assuming the first org address (needs handling if multiple)
      stateId: selectedState ? selectedState.state_id : null,
      countyId: selectedCounty ? selectedCounty.county_id : null,
    };

    try {
      // Making the PUT request to update user details along with access roles, state, and county
      const response = await axios.put(
        `${baseUrl}/app/me/userinfo2/${selectedUser.fusionauth_user_id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
            "x-api-key": apiKey,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Clear all existing roles for the user's applications
        for (const app of applications) {
          await axios.delete(
            `${baseUrl}/app/access/user-roles/${selectedUser.fusionauth_user_id}/${app.app_id}`,
            {
              headers: {
                Authorization: `Bearer ${tokens.accessToken}`,
                "x-api-key": apiKey,
              },
            }
          );
        }

        // Every app's row was just deleted above, so a failure here silently
        // strips that app's access. Collect them and report rather than only
        // logging — the admin needs to know their save was partial.
        const accessFailures = [];

        // Set the new roles and access for each application
        for (const [appName, { enabled, role }] of Object.entries(appAccess)) {
          const app = applications.find((a) => a.app_name === appName);
          if (!app) continue;

          // Sheriff Sale's dropdown carries an SSS role name, which has no
          // matching usermanagement row. Its user_roles entry exists only to
          // grant the portal tile, so any role for that app will do — the role
          // that matters is written to the Sheriff Sale DB separately.
          const roleDetails =
            appName === SHERIFF_SALE_APP_NAME
              ? appRoles[app.app_id]?.[0]
              : appRoles[app.app_id]?.find((r) => r.role_name === role);
          if (enabled && roleDetails) {
            try {
              await axios.post(
                `${baseUrl}/app/access/manage-user-roles`,
                {
                  userId: selectedUser.fusionauth_user_id,
                  roleId: roleDetails.role_id,
                  appId: app.app_id,
                  enabled,
                },
                {
                  headers: {
                    Authorization: `Bearer ${tokens.accessToken}`,
                    "x-api-key": apiKey,
                  },
                }
              );
            } catch (error) {
              console.error("Error updating user roles:", error);
              accessFailures.push(appName);
            }
          } else if (enabled && !roleDetails) {
            // No usermanagement role exists for this app, so no row can be
            // written and the app's tile will not appear.
            accessFailures.push(appName);
          }
        }

        if (accessFailures.length > 0) {
          setShowToast(true);
          setToastInfo({
            header: "Error: Application Access",
            body: `Access could not be saved for: ${accessFailures.join(", ")}. Re-save to try again.`,
          });
          setUpdateStatus({
            success: false,
            message: `Access not saved for ${accessFailures.join(", ")}`,
          });
          return;
        }

        // Second write: the Sheriff Sale user row. The user_roles write above
        // grants the portal tile; this one carries the role the Sheriff Sale
        // app actually reads. Reported separately so a failure here isn't
        // mistaken for the whole save failing.
        try {
          await saveSheriffSaleRole();
        } catch (error) {
          console.error("Error saving Sheriff Sale role:", error);
          const detail =
            error.response?.data?.data ||
            error.response?.data?.message ||
            "Sheriff Sale role could not be saved.";

          setShowToast(true);
          setToastInfo({
            header: "Error: Sheriff Sale",
            body: `Portal access saved, but the Sheriff Sale role was not: ${detail}`,
          });
          setUpdateStatus({ success: false, message: detail });
          return;
        }
      }
      setShowToast(true);
      setToastInfo({
        header: "User Details",
        body: "User details and roles updated successfully.",
      });
      setUpdateStatus({
        success: true,
        message: "User details and roles updated successfully.",
      });
    } catch (error) {
      console.error("Error updating user details:", error);
      const errorMessage =
        error.response?.data?.error || "An unexpected error occurred";
      setUpdateStatus({ success: false, message: errorMessage });
      setShowToast(true);
      setToastInfo({
        header: "Error: User Details",
        body: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

//   const testRevalidate = async (token) => {
//     setIsLoading(true);
//     try {
//       const response = await axios.post(
//         `${baseUrl}/app/proxy/revalidate?refresh_token=${token}`,
//         null,
//         {
//           headers: {
//             "Content-Type": "application/x-www-form-urlencoded",
//             "x-api-key": process.env.REACT_APP_SERVER_API_KEY,
//           },
//         }
//       );
//       console.log("Revalidation response:", response.data);
//     } catch (error) {
//       console.error("Error during revalidation:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

  useEffect(() => {
    if (selectedUser) {
      setUserDetails((prevDetails) => {
        let changesMade = false;
        let newOrganizations = prevDetails.organizations.map((org) => {
          if (org.addresses.length === 0) {
            changesMade = true;
            return { ...org, addresses: [{ ...emptyAddress }] };
          }
          return org;
        });

        if (newOrganizations.length === 0) {
          changesMade = true;
          newOrganizations = [
            { ...emptyOrg, addresses: [{ ...emptyAddress }] },
          ];
        }

        if (changesMade) {
          return { ...prevDetails, organizations: newOrganizations };
        }
        return prevDetails;
      });
    }
  }, [selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="user-management-container" style={{ position: "relative" }}>
          {isLoading && <LoadingAnimation />}
          {/* {!isLoading && updateStatus.message && (
            <div style={{ color: updateStatus.success ? "green" : "red" }}>
              {updateStatus.message}
            </div>
          )} */}

          <div className="header">User Management</div>

          {selectedUser && userDetails.user && (
            <div>
              <div
                className="county-state-select"
                style={{
                  border: "1px solid orange",
                  borderRadius: "5px",
                  margin: "10px",
                  width: "auto",
                  padding: "10px",
                }}
              >
                <Dropdown onSelect={handleStateSelect}>
                  <Dropdown.Toggle variant="success" id="dropdown-basic">
                    {selectedState ? selectedState.state_name : "Select State"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {states.map((state) => (
                      <Dropdown.Item key={state.state_id} eventKey={state.state_id.toString()}>
                        {state.state_name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {selectedState && (
                  <Dropdown onSelect={handleCountySelect}>
                    <Dropdown.Toggle variant="success" id="dropdown-basic">
                      {selectedCounty ? selectedCounty.county_name : "Select County"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {/* The API returns counties in insertion order, which
                          reads as unordered in a list this long. */}
                      {[...counties]
                        .sort((a, b) =>
                          String(a.county_name ?? "").localeCompare(
                            String(b.county_name ?? "")
                          )
                        )
                        .map((county) => (
                          <Dropdown.Item
                            key={county.county_id}
                            eventKey={county.county_id.toString()}
                          >
                            {county.county_name}
                          </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                <Button variant="warning" onClick={clearCountyStateSelection}>
                  Clear User County Association
                </Button>

                {/* Law firm scopes a Sheriff Sale "Law Firm" user to their own
                    firm's cases, so it's only asked for when that role is
                    selected. It lives in the Sheriff Sale DB — there is no
                    usermanagement equivalent to reuse. */}
                {sheriffSaleEnabled && sssRequiredScope === "lawFirm" && (
                  <Dropdown
                    onSelect={(eventKey) =>
                      setSssLawFirmId(eventKey ? Number(eventKey) : null)
                    }
                  >
                    <Dropdown.Toggle variant="success" id="dropdown-lawfirm">
                      {sssLawFirms.find((firm) => firm.idLawFirm === sssLawFirmId)
                        ?.name || "Select Law Firm"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      {sssLawFirms.map((firm) => (
                        <Dropdown.Item
                          key={firm.idLawFirm}
                          eventKey={String(firm.idLawFirm)}
                        >
                          {firm.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>

              <div
                className="app-access-section"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  border: "1px solid orange",
                  borderRadius: "5px",
                  margin: "10px",
                  padding: "10px",
                  gap: "10px", // Adds consistent spacing between items
                }}
              >
                {applications.map((app) => (
                  <Form.Group
                    key={app.app_id}
                    className="d-flex align-items-center"
                    style={{
                      minWidth: "250px", // Ensures a minimum width for each item
                      flex: "1 1 250px", // Allows items to grow and shrink, wrapping as needed
                      marginBottom: "10px", // Adds space below each item
                    }}
                  >
                    <Form.Check
                      type="switch"
                      id={`switch-${app.app_name}`}
                      label={app.app_name}
                      checked={appAccess[app.app_name]?.enabled || false}
                      onChange={() => handleAppToggle(app.app_name)}
                      className="custom-switch"
                      style={{ marginRight: "10px" }} // Adds space between the switch and dropdown
                    />
                    <Dropdown
                      onSelect={(role) => handleRoleChange(app.app_name, role)}
                      disabled={!appAccess[app.app_name]?.enabled}
                    >
                      <Dropdown.Toggle
                        variant="primary"
                        disabled={!appAccess[app.app_name]?.enabled}
                        style={{ minWidth: "120px" }} // Ensures the dropdown has a minimum width
                      >
                        {(app.app_name === SHERIFF_SALE_APP_NAME
                          ? sssRoleName
                          : appAccess[app.app_name]?.role) || "Select Role"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {/* Sheriff Sale roles live in the Sheriff Sale DB, not
                            the usermanagement `roles` table every other app
                            reads, so this one dropdown is fed from core-api. */}
                        {(app.app_name === SHERIFF_SALE_APP_NAME
                          ? sssRoles.map((r) => ({
                              role_id: r.idRole,
                              role_name: r.name,
                            }))
                          : appRoles[app.app_id] ?? []
                        ).map((role) => (
                          <Dropdown.Item key={role.role_id} eventKey={role.role_name}>
                            {role.role_name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>
                ))}
              </div>


              <div
                className="user-info"
                style={{
                  border: "1px solid orange",
                  borderRadius: "5px",
                  margin: "10px",
                  width: "auto",
                  padding: "10px",
                }}
              >
                <Form className="infobox">
                  <Form.Group controlId="userFusionAuthId">
                    <Form.Label className="label-bg">FusionAuth User ID</Form.Label>
                    <Form.Control type="text" readOnly value={userDetails.user.fusionauth_user_id || ""} />
                  </Form.Group>
                  <Form.Group controlId="userFirstName">
                    <Form.Label className="label-bg">First Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="First Name"
                      name="first_name"
                      value={userDetails.user.first_name || ""}
                      onChange={handleUserDetailChange}
                    />
                  </Form.Group>
                  <Form.Group controlId="userMiddleName">
                    <Form.Label className="label-bg">Middle Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Middle Name"
                      name="middle_name"
                      value={userDetails.user.middle_name || ""}
                      onChange={handleUserDetailChange}
                    />
                  </Form.Group>
                  <Form.Group controlId="userLastName">
                    <Form.Label className="label-bg">Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Last Name"
                      name="last_name"
                      value={userDetails.user.last_name || ""}
                      onChange={handleUserDetailChange}
                    />
                  </Form.Group>
                  <Form.Group controlId="userEmail">
                    <Form.Label className="label-bg">Email</Form.Label>
                    <Form.Control type="email" readOnly value={userDetails.user.email || ""} />
                  </Form.Group>
                  <Form.Group controlId="userPhone">
                    <Form.Label className="label-bg">Phone</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Phone"
                      name="phone"
                      value={userDetails.user.phone || ""}
                      onChange={handleUserDetailChange}
                    />
                  </Form.Group>
                  <Form.Group controlId="userFax">
                    <Form.Label className="label-bg">Fax</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Fax"
                      name="fax"
                      value={userDetails.user.fax || ""}
                      onChange={handleUserDetailChange}
                    />
                  </Form.Group>
                  {userDetails.user.addresses.map((address, index) => (
                    <React.Fragment key={index}>
                      <Form.Group controlId={`userAddressStreet${index}`}>
                        <Form.Label className="label-bg">Street Address</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Street Address"
                          name="street_address"
                          value={address.street_address || ""}
                          onChange={(e) => handleUserDetailChange(e, index, "street_address")}
                        />
                      </Form.Group>
                      <Form.Group controlId={`userAddressCity${index}`}>
                        <Form.Label className="label-bg">City</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="City"
                          name="city"
                          value={address.city || ""}
                          onChange={(e) => handleUserDetailChange(e, index, "city")}
                        />
                      </Form.Group>
                      <Form.Group controlId={`userAddressState${index}`}>
                        <Form.Label className="label-bg">State</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="State"
                          name="state"
                          value={address.state || ""}
                          onChange={(e) => handleUserDetailChange(e, index, "state")}
                        />
                      </Form.Group>
                      <Form.Group controlId={`userAddressZip${index}`}>
                        <Form.Label className="label-bg">Zip Code</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Zip Code"
                          name="zip"
                          value={address.zip || ""}
                          onChange={(e) => handleUserDetailChange(e, index, "zip")}
                        />
                      </Form.Group>
                    </React.Fragment>
                  ))}
                </Form>
              </div>

              {userDetails.organizations.map((org, orgIndex) => (
                <div
                  key={orgIndex}
                  className="organization-info"
                  style={{
                    border: "1px solid orange",
                    borderRadius: "5px",
                    margin: "10px",
                    padding: "10px",
                  }}
                >
                  <Form className="infobox">
                    <Form.Group controlId={`orgName${orgIndex}`}>
                      <Form.Label className="label-bg">Organization Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="business_name"
                        value={org.business_name || ""}
                        onChange={(e) => handleOrganizationDetailChange(e, orgIndex)}
                      />
                    </Form.Group>
                    <Form.Group controlId={`orgFirstName${orgIndex}`}>
                      <Form.Label className="label-bg">First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={org.first_name || ""}
                        onChange={(e) => handleOrganizationDetailChange(e, orgIndex)}
                      />
                    </Form.Group>
                    <Form.Group controlId={`orgMiddleName${orgIndex}`}>
                      <Form.Label className="label-bg">Middle Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="middle_name"
                        value={org.middle_name || ""}
                        onChange={(e) => handleOrganizationDetailChange(e, orgIndex)}
                      />
                    </Form.Group>
                    <Form.Group controlId={`orgLastName${orgIndex}`}>
                      <Form.Label className="label-bg">Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={org.last_name || ""}
                        onChange={(e) => handleOrganizationDetailChange(e, orgIndex)}
                      />
                    </Form.Group>
                    <Form.Group controlId={`orgPhone${orgIndex}`}>
                      <Form.Label className="label-bg">Phone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={org.phone || ""}
                        onChange={(e) => handleOrganizationDetailChange(e, orgIndex)}
                      />
                    </Form.Group>
                    <Form.Group controlId={`orgFax${orgIndex}`}>
                      <Form.Label className="label-bg">Fax</Form.Label>
                      <Form.Control
                        type="text"
                        name="fax"
                        value={org.fax || ""}
                        onChange={(e) => handleOrganizationDetailChange(e, orgIndex)}
                      />
                    </Form.Group>

                    {org.addresses.map((address, addrIndex) => (
                      <React.Fragment key={addrIndex}>
                        <Form.Group controlId={`orgStreetAddress${orgIndex}-${addrIndex}`}>
                          <Form.Label className="label-bg">Street Address</Form.Label>
                          <Form.Control
                            type="text"
                            name="street_address"
                            value={address.street_address || ""}
                            onChange={(e) => handleOrganizationDetailChange(e, orgIndex, addrIndex, "street_address")}
                          />
                        </Form.Group>
                        <Form.Group controlId={`orgCity${orgIndex}-${addrIndex}`}>
                          <Form.Label className="label-bg">City</Form.Label>
                          <Form.Control
                            type="text"
                            name="city"
                            value={address.city || ""}
                            onChange={(e) => handleOrganizationDetailChange(e, orgIndex, addrIndex, "city")}
                          />
                        </Form.Group>
                        <Form.Group controlId={`orgState${orgIndex}-${addrIndex}`}>
                          <Form.Label className="label-bg">State</Form.Label>
                          <Form.Control
                            type="text"
                            name="state"
                            value={address.state || ""}
                            onChange={(e) => handleOrganizationDetailChange(e, orgIndex, addrIndex, "state")}
                          />
                        </Form.Group>
                        <Form.Group controlId={`orgZip${orgIndex}-${addrIndex}`}>
                          <Form.Label className="label-bg">Zip Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="zip"
                            value={address.zip || ""}
                            onChange={(e) => handleOrganizationDetailChange(e, orgIndex, addrIndex, "zip")}
                          />
                        </Form.Group>
                      </React.Fragment>
                    ))}
                  </Form>
                </div>
              ))}
            </div>
          )}

          {/* <Form.Group controlId="testUserId">
                <Form.Label>Test Fetch User Details</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter User ID"
                    value={testUserId}
                    onChange={(e) => setTestUserId(e.target.value)}
                />
                <Button onClick={testFetchUserDetails}>Fetch User Details</Button>
            </Form.Group>
            <Form.Group controlId="testToken">
                <Form.Label>Test Token</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter Token"
                    value={testToken}
                    onChange={(e) => setTestToken(e.target.value)}
                />
                <Button onClick={() => testRevalidate(testToken)}>Test Revalidate</Button>  
            </Form.Group> */}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button className="btn-primary" onClick={updateUserDetails}>
          Update User Details
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserEditModal;